import React, {
  createContext,
  useState,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import LogRocket from 'logrocket';
import { useFeatureFlagRedux } from '../../../../hooks';
import {
  capturePayment,
  waitForReaderConnection,
  createPaymentIntent,
  ICreatePaymentResult,
  inIOSApp,
  processPayment,
} from '../../../../modules/TapToPay';
import {
  StripeTerminalAlreadyCheckedOutError,
  StripeTerminalCaptureError,
  StripeTerminalCollectPaymentError,
  StripeTerminalError,
  StripeTerminalProcessPaymentError,
  PaymentIntent,
  StripeTerminalCanceledError,
  selectors as stripeTerminalSelectors,
  StripeTerminalCommandNotAllowedDuringCallError,
  StripeTerminalConnectError,
} from '../../../../store/StripeTerminal';
import { ProAppointment } from '../../../../api/Providers/Appointments';
import {
  TAP_TO_PAY_APPOINTMENT_GENERIC_ERROR_CODE,
  TAP_TO_PAY_CARD_ERROR_TEST_SWITCH,
  TAP_TO_PAY_CHECKOUT_CONNECTION_ERROR_TEST_SWITCH,
  TAP_TO_PAY_FEATURE_FLAG,
  TAP_TO_PAY_PAYMENT_ERROR_TEST_SWITCH,
  TAP_TO_PAY_TRANSACTION_ERROR_TEST_SWITCH,
} from './constants';
import { FeatureFlagData } from '../../../../hooks/useFeatureFlagRedux';
import { IRootDispatch } from '../../../../store/models';
import store from '../../../../store';
import { SSFetchError } from '../../../../modules/ssFetch';
import { log } from '../../../../modules/TapToPay/TapToPayLogger';

export enum ReaderStatus {
  Ready,
  Done,
  Initializing,
  ProcessingPayment,
  Connecting,
}

export interface ITapToPayContext {
  readerStatus: ReaderStatus;
  setReaderStatus: (readerStatus: ReaderStatus) => void;
  isEnabled: boolean;
  showTapToPayFlow: boolean;
  transactionErrorFlag: FeatureFlagData | null;
  startTapToPayFlow: () => void;
  closeTapToPayFlow: () => void;
  createPayment: (
    appointment: ProAppointment,
    amount: number,
    tipAmount: number,
  ) => Promise<ICreatePaymentResult>;
  checkout: (
    appointmentId: number,
    paymentIntent: PaymentIntent
  ) => Promise<PaymentIntent>;
}

export const TapToPayContext = createContext<ITapToPayContext>({
  readerStatus: ReaderStatus.Ready,
  setReaderStatus: () => { },
  isEnabled: false,
  showTapToPayFlow: false,
  transactionErrorFlag: null,
  createPayment: () => Promise.reject(new TypeError('CreatePayment uninitialized')),
  checkout: () => Promise.reject(new TypeError('Checkout uninitialized')),
  startTapToPayFlow: () => { },
  closeTapToPayFlow: () => { },
});

type TapToPayProviderParams = {
  providerId: number,
  isSimulated: boolean,
  children: React.ReactNode,
};

export function TapToPayProvider({
  providerId,
  children,
  isSimulated = false,
}: TapToPayProviderParams) {
  const [showTapToPayFlow, setShowTapToPayFlow] = useState<boolean>(false);
  const [readerStatus, setReaderStatus] = useState<ReaderStatus>(ReaderStatus.Ready);
  const dispatch = useDispatch<IRootDispatch>();

  const tapToPayFeatureFlag = useFeatureFlagRedux(TAP_TO_PAY_FEATURE_FLAG);
  const cardErrorFlag = useFeatureFlagRedux(TAP_TO_PAY_CARD_ERROR_TEST_SWITCH);
  const paymentErrorFlag = useFeatureFlagRedux(TAP_TO_PAY_PAYMENT_ERROR_TEST_SWITCH);
  const transactionErrorFlag = useFeatureFlagRedux(TAP_TO_PAY_TRANSACTION_ERROR_TEST_SWITCH);
  const connectionErrorFlag = useFeatureFlagRedux(TAP_TO_PAY_CHECKOUT_CONNECTION_ERROR_TEST_SWITCH);

  // should be a combination based on flag and device's capability
  const flags = [
    tapToPayFeatureFlag,
    cardErrorFlag,
    paymentErrorFlag,
    transactionErrorFlag,
  ];
  const isLoaded = flags.every(({ loading }) => !loading);
  const isEnabled = isLoaded && tapToPayFeatureFlag.enabled;

  // shows the tap to pay flow
  const startTapToPayFlow = useCallback(() => setShowTapToPayFlow(true), []);
  const closeTapToPayFlow = useCallback(() => setShowTapToPayFlow(false), []);

  const createPayment = useCallback(async (
    appointment: ProAppointment,
    amount: number,
    tipAmount: number = 0,
  ): Promise<ICreatePaymentResult> => {
    let paymentIntent: ICreatePaymentResult;

    try {
      paymentIntent = await createPaymentIntent({
        appointment,
        amount,
        tipAmount,
      });
    } catch (e) {
      throw new StripeTerminalError(e);
    }
    return paymentIntent;
  }, []);

  // TODO(BD): Consider moving this into ProviderManageAppointment model as checkoutTapToPay effect
  const checkout = useCallback(async (
    appointmentId: number,
    initPaymentIntent: PaymentIntent,
  ): Promise<PaymentIntent> => {
    let paymentIntent: PaymentIntent = initPaymentIntent;

    if (isSimulated && paymentErrorFlag.enabled) {
      throw new StripeTerminalCollectPaymentError('Simulated error due to payment error flag');
    }

    setReaderStatus(ReaderStatus.Connecting);
    log('Checking for Reader Connection');

    try {
      if (inIOSApp()) {
        await waitForReaderConnection({ getState: store.getState });
      } else if (isSimulated && connectionErrorFlag.enabled) {
        throw new StripeTerminalConnectError('Simulated No Reader Connection error due to checkout connection error flag');
      }
    } catch (e) {
      if (e instanceof StripeTerminalError) {
        throw new StripeTerminalCollectPaymentError(`Connection check failed: ${e.message}`);
      } else {
        throw new StripeTerminalCollectPaymentError(`Connection check failed: ${e}`);
      }
    }

    try {
      if (inIOSApp()) {
        log('Collecting Payment Method via Tap to Pay on iPhone…');
        // Try to smooth the wording on the screen while we transition to Apple's Tap to Pay screen
        // and back. When the screen closes, the plugin makes the call to Stripe to confirm the
        // payment intent, so in most cases this will tell ReaderStatusIndicator to show "Processing
        // payment" instead of "Initializing pay screen."
        setTimeout(
          () => {
            setReaderStatus(ReaderStatus.ProcessingPayment);
          }, 1500,
        );
        await dispatch.stripeTerminal.collectPayment({ clientSecret: paymentIntent.client_secret });
      } else if (isSimulated) {
        log('Collecting Payment Method via Simulated Tap…');
        setReaderStatus(ReaderStatus.ProcessingPayment);
        await processPayment(paymentIntent.id);
      }
    } catch (e) {
      if (e instanceof StripeTerminalCanceledError) {
        /** The StripeTerminalCanceledError thrown from the collectPayment effect did not contain
         * the latest readerDisplayMessage, because the latestReaderDisplayMessage was set from
         * _outside_ of the effect in the handleConnectReaderSuccess callback. Hence, it was
         * not available to us in the collectPayment effect, and we have to update it here.
         *
         * Lastly, we cannot use useSelector as we absolutely cannot allow the component to rerender
         * during the checkout flow everytime we update latestReaderDisplayMessage.
         */
        e.updateMessage(
          stripeTerminalSelectors.latestReaderDisplayMessage(store.getState() as any),
        );
        throw e;
      } else if (e instanceof StripeTerminalProcessPaymentError
        || e instanceof StripeTerminalCollectPaymentError
        || e instanceof StripeTerminalCommandNotAllowedDuringCallError) {
        throw (e);
      } else {
        throw new StripeTerminalCollectPaymentError(e);
      }
    }

    if (isSimulated && cardErrorFlag.enabled) {
      throw new StripeTerminalProcessPaymentError('Simulated error due to process payment flag');
    }

    log('Capturing Payment…');
    try {
      paymentIntent = await capturePayment(
        providerId,
        appointmentId,
        paymentIntent.id,
      );
      LogRocket.track('Tap to Pay Checkout Call To capturePayment Succeeded');
    } catch (e: unknown) {
      log(JSON.stringify(e));
      if (e instanceof SSFetchError && e.code === TAP_TO_PAY_APPOINTMENT_GENERIC_ERROR_CODE) {
        LogRocket.track('Tap to Pay Checkout capturePayment Failed Already Checked Out');
        throw new StripeTerminalAlreadyCheckedOutError(e);
      } else {
        LogRocket.track('Tap to Pay Checkout capturePayment Failed');
        throw new StripeTerminalCaptureError(e);
      }
    } finally {
      setReaderStatus(ReaderStatus.Done);
    }

    return paymentIntent;
  }, [
    isSimulated,
    dispatch.stripeTerminal,
    providerId,
    cardErrorFlag.enabled,
    paymentErrorFlag.enabled,
    connectionErrorFlag.enabled,
  ]);

  const value = {
    isEnabled,
    showTapToPayFlow,
    transactionErrorFlag,
    createPayment,
    checkout,
    readerStatus,
    startTapToPayFlow,
    closeTapToPayFlow,
    setReaderStatus: (status: ReaderStatus) => { setReaderStatus(status); },
  };

  if (!isEnabled) return null;

  return (
    <TapToPayContext.Provider value={value}>{children}</TapToPayContext.Provider>
  );
}
